import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { CONVERSATION_THREAD_COMMENT_IMAGE_EMBED_ADD } from '../../../../utils/apollo/conversations';
import {
  IConversationThreadCommentImageEmbedAddMutation,
  IConversationThreadCommentImageEmbedAddMutationVariables,
  IImageEmbedFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../../utils/types';
import {
  IImageEmbedUploadHandle,
  IImageEmbedUploadProps,
} from '../../../ImageUploady/ImageEmbedUpload';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';

const useConversationImageEmbed = (
  defaultImageEmbed: IImageEmbedFieldsFragment | null,
) => {
  const [uploadedImagedEmbed, setUploadedImagedEmbed] =
    useState<IImageEmbedFieldsFragment | null>(defaultImageEmbed);

  const [addImageEmbedMutation] = useMutation<
    IConversationThreadCommentImageEmbedAddMutation,
    IConversationThreadCommentImageEmbedAddMutationVariables
  >(CONVERSATION_THREAD_COMMENT_IMAGE_EMBED_ADD);

  const imageEmbedUploadHandle = useRef<IImageEmbedUploadHandle>(null);

  const startImageEmbedUpload = useCallback(() => {
    assertNotNil(imageEmbedUploadHandle.current);
    imageEmbedUploadHandle.current.startUpload();
  }, []);

  const createImageEmbedUploadConfig = useCallback(async () => {
    const resp = await addImageEmbedMutation();
    const result = resp.data?.conversationThreadCommentImageEmbedAdd;
    if (printGraphqlErrors(result?.errors)) {
      throw new Error('Failed to create image embed upload config');
    }
    assertNotNil(result?.uploadConfig);
    return result.uploadConfig;
  }, [addImageEmbedMutation]);

  const removeImageEmbed = () => {
    setUploadedImagedEmbed(null);
  };

  const imageEmbedUploadProps = useMemo(
    () =>
      ({
        ref: imageEmbedUploadHandle,
        onUploadSuccess: setUploadedImagedEmbed,
        createImageEmbedUploadConfig,
      }) satisfies IImageEmbedUploadProps,
    [createImageEmbedUploadConfig],
  );

  return {
    imageEmbedUploadProps,
    startImageEmbedUpload,
    uploadedImagedEmbed,
    removeImageEmbed,
  };
};

export default useConversationImageEmbed;
